/**
 * Admin CRUD + on-demand crawl for vacancy sources; public subscribe /
 * confirm / unsubscribe flow for vacancy alert emails.
 */
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { createClient } from "@supabase/supabase-js";

function publicClient() {
  return createClient(
    process.env.SUPABASE_URL ?? import.meta.env.VITE_SUPABASE_URL!,
    process.env.SUPABASE_PUBLISHABLE_KEY ?? import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY!,
  );
}

async function assertAdmin(ctx: { supabase: any; userId: string }) {
  const { data, error } = await ctx.supabase.rpc("has_role", {
    _user_id: ctx.userId,
    _role: "admin",
  });
  if (error) throw new Error(error.message);
  if (!data) throw new Error("Forbidden");
}

// -------- Admin: list sources with recent run stats --------
export const adminListVacancySources = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context);
    const { data, error } = await context.supabase
      .from("vacancy_sources")
      .select("*")
      .order("employer", { ascending: true });
    if (error) throw new Error(error.message);
    return data ?? [];
  });

// -------- Admin: recent runs --------
export const adminListVacancyRuns = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context);
    const { data, error } = await context.supabase
      .from("vacancy_crawl_runs")
      .select("*")
      .order("started_at", { ascending: false })
      .limit(50);
    if (error) throw new Error(error.message);
    return data ?? [];
  });

// -------- Admin: upsert source --------
const SourceSchema = z.object({
  id: z.string().uuid().optional(),
  employer: z.string().min(2).max(200),
  careers_url: z.string().url().max(500),
  sector: z.string().max(120).optional().nullable(),
  is_active: z.boolean().optional(),
  respect_robots: z.boolean().optional(),
  crawl_interval_minutes: z.number().int().min(15).max(1440).optional(),
  notes: z.string().max(1000).optional().nullable(),
});
export const adminUpsertVacancySource = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => SourceSchema.parse(d))
  .handler(async ({ data, context }) => {
    await assertAdmin(context);
    if (data.id) {
      const { id, ...patch } = data;
      const { error } = await context.supabase.from("vacancy_sources").update(patch).eq("id", id);
      if (error) throw new Error(error.message);
      return { id };
    }
    const { data: row, error } = await context.supabase
      .from("vacancy_sources")
      .insert({ ...data, created_by: context.userId })
      .select("id")
      .single();
    if (error) throw new Error(error.message);
    return { id: row.id };
  });

export const adminDeleteVacancySource = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    await assertAdmin(context);
    const { error } = await context.supabase.from("vacancy_sources").delete().eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

// -------- Admin: crawl one source now --------
export const adminRunVacancySource = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    await assertAdmin(context);
    const { crawlSource } = await import("@/lib/vacancy-crawler.server");
    return crawlSource(data.id);
  });

// -------- Admin: crawl all due now --------
export const adminRunAllVacancySources = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context);
    const { crawlDueSources } = await import("@/lib/vacancy-crawler.server");
    return crawlDueSources(20);
  });

// -------- Admin: subscribers summary --------
export const adminListVacancySubscribers = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context);
    const { data, error } = await context.supabase
      .from("vacancy_subscribers")
      .select("id, email, sector, location_contains, keyword, confirmed_at, unsubscribed_at, last_notified_at, notifications_sent, created_at")
      .order("created_at", { ascending: false })
      .limit(500);
    if (error) throw new Error(error.message);
    return data ?? [];
  });

// -------- Public: subscribe (creates unconfirmed row, sends confirm email) --------
const SubscribeSchema = z.object({
  email: z.string().email().max(200),
  sector: z.string().max(120).optional().nullable(),
  location_contains: z.string().max(120).optional().nullable(),
  keyword: z.string().max(120).optional().nullable(),
});

export const publicSubscribeVacancies = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) => SubscribeSchema.parse(d))
  .handler(async ({ data }) => {
    const sb = publicClient();
    const email = data.email.trim().toLowerCase();
    // Try insert; if exists, fetch existing tokens to resend confirmation
    const { data: inserted, error: insErr } = await sb
      .from("vacancy_subscribers")
      .insert({
        email,
        sector: data.sector || null,
        location_contains: data.location_contains || null,
        keyword: data.keyword || null,
      })
      .select("id, confirm_token, unsub_token, confirmed_at")
      .maybeSingle();

    let row = inserted;
    if (insErr && !row) {
      // Duplicate — admin-side fetch to resend
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      const { data: existing } = await (supabaseAdmin as any)
        .from("vacancy_subscribers")
        .select("id, confirm_token, unsub_token, confirmed_at")
        .eq("email", email)
        .maybeSingle();
      row = existing ?? null;
    }
    if (!row) throw new Error("Could not subscribe — please try again.");

    // If already confirmed, no email needed
    if (row.confirmed_at) return { ok: true, alreadyConfirmed: true };

    // Send confirmation email via admin (bypasses RLS)
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { enqueueTransactionalEmail } = await import("@/lib/email/enqueue.server");
    const confirmUrl = `https://nkadimengdannylcs.com/careers/subscribe/confirm?token=${row.confirm_token}`;
    const unsubUrl = `https://nkadimengdannylcs.com/careers/unsubscribe?token=${row.unsub_token}`;
    // Reuse vacancy-alert template as a shell with an intro message via a
    // single-job placeholder → keep it simple: use welcome template instead
    // isn't ideal, so send a plain email via the assistant admin channel.
    // For now, log the confirm link server-side and mark ok — the templated
    // confirmation email uses `admin-reply`-style shell.
    void supabaseAdmin; // referenced for future use
    await enqueueTransactionalEmail({
      templateName: "vacancy-alert",
      recipientEmail: email,
      idempotencyKey: `vac-confirm-${row.id}`,
      subjectOverride: "Confirm your vacancy alerts",
      templateData: {
        jobs: [
          {
            title: "Confirm your subscription",
            employer: "Nkadimeng Danny Legal",
            location: null,
            employment_type: null,
            closing_date: null,
            application_url: confirmUrl,
            description:
              "Click the button below to confirm your email address and start receiving vacancy alerts. You can unsubscribe at any time.",
          },
        ],
        unsubscribeUrl: unsubUrl,
      },
      label: "vacancy-alert-confirm",
    });

    return { ok: true, alreadyConfirmed: false };
  });

// -------- Public: confirm subscription --------
export const publicConfirmVacancySubscription = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) => z.object({ token: z.string().min(10).max(200) }).parse(d))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const sb = supabaseAdmin as any;
    const { data: row } = await sb
      .from("vacancy_subscribers")
      .select("id, confirmed_at")
      .eq("confirm_token", data.token)
      .maybeSingle();
    if (!row) return { ok: false, error: "invalid_token" };
    if (row.confirmed_at) return { ok: true, alreadyConfirmed: true };
    const { error } = await sb
      .from("vacancy_subscribers")
      .update({ confirmed_at: new Date().toISOString(), unsubscribed_at: null })
      .eq("id", row.id);
    if (error) return { ok: false, error: error.message };
    return { ok: true, alreadyConfirmed: false };
  });

// -------- Public: unsubscribe --------
export const publicUnsubscribeVacancy = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) => z.object({ token: z.string().min(10).max(200) }).parse(d))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const sb = supabaseAdmin as any;
    const { data: row } = await sb
      .from("vacancy_subscribers")
      .select("id")
      .eq("unsub_token", data.token)
      .maybeSingle();
    if (!row) return { ok: false, error: "invalid_token" };
    const { error } = await sb
      .from("vacancy_subscribers")
      .update({ unsubscribed_at: new Date().toISOString(), confirmed_at: null })
      .eq("id", row.id);
    if (error) return { ok: false, error: error.message };
    return { ok: true };
  });
